import { Button } from "../buttons/Button";
import LogoCropped from "../../../assets/images/MarTrakCropped.png"
import Logo from "../../../assets/images/MarTrakPNG.png"
import { useNavigate } from "react-router-dom"
import usePermissions from "../../../hooks/usePermissions"
import { menuItems } from "layouts/menuOptions";
import { useUser } from "../../../contexts/UserContext";
import { useSidebar } from "../../../contexts/SidebarContext";
import { useUnsavedChanges } from "../../../contexts/UnsavedChangesContext";
import { TooltipButton } from "../buttons/TooltipButton";
import { useRef, useEffect } from "react";

export default function Sidebar() {
  const {isOpen, setIsOpen, isMobile} = useSidebar()
  const sidebarRef = useRef()

  const navigate = useNavigate();
  const { hasPermission, isInGroup, isExternal } = usePermissions()
  const{ logout, hasSpeedApprovalIndicator} = useUser()

  const showSpeedApprovalIndicator = Object.values(hasSpeedApprovalIndicator).some(Boolean);

  const {hasUnsavedChanges, requestConfirm} = useUnsavedChanges()

  const currentPage = window.location.pathname
 
  // tailwind classes
  const iconClasses = "mr-2 text-purple-800"

  const sidebarClass =
    isMobile
      ? isOpen
        ? "fixed top-0 bottom-0 left-0 h-full w-64 bg-gray-100 text-white transition-all duration-200 z-40 overflow-y-auto shadow-gray-300 shadow-md"
        : "fixed top-0 bottom-0 left-[-300px] h-full w-64 bg-gray-100 text-white transition-all duration-200 z-40"
      : isOpen
        ? "top-0 w-72 bg-gray-100 bottom-0 transition-all duration-200 z-40 h-full shadow-gray-300 shadow-md overflow-y-auto"
        : `top-0 w-16 bg-gray-100 bottom-0 transition-all duration-200 z-40 h-full overflow-y-auto overflow-x-hidden`;

  const mobileButtonClass = "text-sm mt-2 mb-2"

  // navigation
  const handleNavigate = async(path) => {
    if(hasUnsavedChanges){
        const isConfirmed = await requestConfirm("This page has unsaved work - are you sure you want to cancel?")

        if(!isConfirmed) return;
    }
    navigate(path)

    setIsOpen(false)
  }

  //add listener - if sidebar is open, and user clicks the page (outside of sidebar), the sidebar will close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  return (
      <aside 
        className={sidebarClass}
        ref={sidebarRef}
      >
        
        <nav className="flex flex-col items-start p-2 space-y-2 pt-4">
        
            {/* LOGO */}

            <div className="flex items-center p-3">
              {/* show cropped martrak 'M' on collapsed icon menu - otherwise, show full martrak logo */}
              {currentPage !== "/" &&  isOpen ? 
                  <img 
                    src={Logo} 
                    alt="MarTrak logo" 
                    className="w-44 hover:cursor-pointer border-right border-1 border-gray"
                    onClick={() => handleNavigate('/')}
                  />
                  
                  : currentPage !== "/"&& !isMobile ?  
                    
                    <img 
                      src={LogoCropped} 
                      alt="MarTrak logo" 
                      className="w-8 min-w-[20px] hover:cursor-pointer border-right border-1 border-gray"
                      onClick={() => handleNavigate('/')}
                    />

                  : null
              }
            </div>

            {/* OPEN/CLOSE MENU BUTTON */}

            <Button variant="noBackground" onClick={() => setIsOpen(!isOpen)} className="text-left">
                <i className={`fa-solid fa-bars ${iconClasses}`} ></i>
            </Button>

            {/* BUTTONS FOR MENU ITEMS */}

            {/* on desktop size, if menu closed, show collapsed bar with icons only */}
            {menuItems.map(({ label, icon, path, permission, groups }) => {
                if (
                    (permission && !hasPermission(permission.resource, permission.action)) ||
                    (groups && !isInGroup(groups))
                ) {
                    return null;
                }

                return (
                  <TooltipButton
                    key={label}
                    variant="noBackground"
                    onClick={() => handleNavigate(path)}
                    className={`text-left ${ isMobile ? mobileButtonClass : ""}`}
                    tooltipLabel={label}
                    tooltipDisabled={isOpen}
                  >
                    <div className="relative inline-flex items-center">
                      <i className={`fa-solid ${icon} ${iconClasses}`}></i>
                      {isOpen && <span className="hover:underline">{label}</span>}

                      {label === "Speed Approvals" && showSpeedApprovalIndicator === true && <i className={`fa-solid fa-bell text-amber-500 text-sm text-[12px] absolute -top-1 -right-1.5 pointer-events-none ${isOpen && 'ml-2 -top-0.5 -right-6'}`}></i>}
                    </div>
                  </TooltipButton>
                );
              })}

            {/* download pdf when help button clicked */}
            <TooltipButton
                key="help-button"
                variant="noBackground"
                className={`text-left ${ isMobile ? mobileButtonClass : ""}`}
                tooltipLabel="Help"
                tooltipDisabled={isOpen}
            >
              
              {isExternal() ? (
                <a href="/Martrak-How-To-Guide-Version2-External.pdf" download={true}>
                  <i className={`fa-regular fa-circle-question ${iconClasses}`}></i>
                  {isOpen && "Help"}
                </a>
              ) : (
                <a href="/Martrak-How-To-Guide-Version2.pdf" download={true}>
                  <i className={`fa-regular fa-circle-question ${iconClasses}`}></i>
                  {isOpen && "Help"}
                </a>
              )}
            </TooltipButton>

            {/* show logout button in sidebar on mobile - on desktop, show on page header */}
            {isMobile && (
              <TooltipButton
                  onClick={() => {
                    setIsOpen(false)
                    logout()
                  }}
                  variant="noBackground"
                  className={`text-left ${ isMobile ? mobileButtonClass : ""}`}
                  tooltipLabel="Logout"
                  tooltipDisabled={isOpen}
                >
                  <i className={`fa-solid fa-right-from-bracket ${iconClasses}`}></i>

                  Log out
              </TooltipButton>
            )}
        </nav>
      </aside>

  );
}
